import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { safeFetch } from '../lib/api';
import { getActiveWorkspaceId } from '../lib/workspaces';
import {
  filterByWorkspace,
  findProgram,
  normaliseProgramList,
  type ProgramConfig,
  type ProgramDefaults,
} from '../lib/programConfig';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * usePrograms — the only supported way to read the program catalogue
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fixed here (see PROGRAM_CONFIG_OVERHAUL.md):
 *
 *  • WORKSPACE BLEED — the previous implementation fell back to
 *    `getDocs(collection(db, 'programs'))` with no workspace filter, so a
 *    tenant whose API call failed could read another tenant's catalogue. The
 *    Firestore fallback is now constrained by workspaceId and the result set is
 *    filtered again client-side.
 *
 *  • SECOND SOURCE OF TRUTH — the old hook merged an unrelated `services`
 *    collection into the program list, so a "program" could originate from two
 *    different collections with different shapes. Removed.
 *
 *  • SHAPE DRIFT — two different `Program` interfaces existed (one declaring
 *    `terms: string[]`, one `terms: ProgramTerm[]`). Every record now passes
 *    through `normaliseProgram()` and comes back as a single `ProgramConfig`.
 *
 *  • Adds `defaults` (workspace / global fallback content) so consumers can
 *    resolve Terms & Conditions and other content without a second fetch.
 */

/** @deprecated Use `ProgramConfig` — kept so existing imports keep compiling. */
export type Program = ProgramConfig;
export type { ProgramConfig, ProgramDefaults };
export type { ProgramIntake as ProgramTerm } from '../lib/programConfig';

export interface ServiceProvider {
  id: string;
  name: string;
  code?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  country?: string;
  isActive: boolean;
  supportedPrograms: string[];
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
}

function normaliseProviders(raw: unknown): ServiceProvider[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
    .map((entry) => ({
      id: String(entry.id ?? entry.providerId ?? ''),
      name: String(entry.name ?? ''),
      code: entry.code ? String(entry.code) : undefined,
      contactName: entry.contactName ? String(entry.contactName) : undefined,
      contactEmail: entry.contactEmail ? String(entry.contactEmail) : undefined,
      contactPhone: entry.contactPhone ? String(entry.contactPhone) : undefined,
      website: entry.website ? String(entry.website) : undefined,
      country: entry.country ? String(entry.country) : undefined,
      isActive: entry.isActive === undefined ? entry.active !== false : entry.isActive !== false,
      supportedPrograms: Array.isArray(entry.supportedPrograms)
        ? entry.supportedPrograms.map(String)
        : [],
      notes: entry.notes ? String(entry.notes) : undefined,
    }))
    .filter((provider) => provider.id && provider.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export interface UseProgramsResult {
  programs: ProgramConfig[];
  activePrograms: ProgramConfig[];
  /** @deprecated Renamed to `activePrograms`. */
  openPrograms: ProgramConfig[];
  providers: ServiceProvider[];
  activeProviders: ServiceProvider[];
  /** Workspace / global fallback content for blank program fields. */
  defaults: ProgramDefaults;
  workspaceId: string;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Resolve by id, programId, code or name. */
  getProgram: (identifier: string | null | undefined) => ProgramConfig | null;
}

export function usePrograms(): UseProgramsResult {
  const [programs, setPrograms] = useState<ProgramConfig[]>([]);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [defaults, setDefaults] = useState<ProgramDefaults>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const workspaceId = getActiveWorkspaceId();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    let programList: ProgramConfig[] = [];
    let providerList: ServiceProvider[] = [];
    let programDefaults: ProgramDefaults = {};

    // 1. Authenticated API — already workspace-scoped via the X-Workspace-Id
    //    header attached by safeFetch().
    try {
      const [programsData, providersData] = await Promise.all([
        safeFetch('/api/admin/programs').catch(() => null),
        safeFetch('/api/admin/service-providers').catch(() => null),
      ]);

      if (programsData) {
        programList = normaliseProgramList(programsData.programs);
        if (programsData.defaults && typeof programsData.defaults === 'object') {
          programDefaults = programsData.defaults as ProgramDefaults;
        }
      }
      if (providersData) {
        providerList = normaliseProviders(providersData.providers ?? providersData.serviceProviders);
      }
    } catch (err) {
      console.warn('[usePrograms] API fetch failed, falling back to Firestore:', err);
    }

    // 2. Firestore fallback — CONSTRAINED BY WORKSPACE. The previous version
    //    read the whole collection, which leaked other tenants' programs.
    if (programList.length === 0) {
      try {
        const programsRef = collection(db, 'programs');
        const snap = await getDocs(
          workspaceId ? query(programsRef, where('workspaceId', '==', workspaceId)) : programsRef
        );
        const raw: unknown[] = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.deletedAt == null) raw.push({ id: docSnap.id, ...data });
        });
        programList = filterByWorkspace(normaliseProgramList(raw), workspaceId);
      } catch (err) {
        console.warn('[usePrograms] Firestore program fallback failed:', err);
        setError('Programs could not be loaded.');
      }
    }

    if (providerList.length === 0) {
      try {
        const snap = await getDocs(collection(db, 'serviceProviders'));
        const raw: unknown[] = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.deletedAt == null) raw.push({ id: docSnap.id, ...data });
        });
        providerList = normaliseProviders(raw);
      } catch {
        // Quiet fallback when unauthenticated or offline
      }
    }

    setPrograms(filterByWorkspace(programList, workspaceId));
    setProviders(providerList);
    setDefaults(programDefaults);
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const activePrograms = useMemo(() => programs.filter((p) => p.active), [programs]);
  const activeProviders = useMemo(() => providers.filter((p) => p.isActive), [providers]);
  const getProgram = useCallback(
    (identifier: string | null | undefined) => findProgram(programs, identifier),
    [programs]
  );

  return {
    programs,
    activePrograms,
    openPrograms: activePrograms,
    providers,
    activeProviders,
    defaults,
    workspaceId,
    loading,
    error,
    refresh: fetchData,
    getProgram,
  };
}

/**
 * Single-program accessor for screens that only care about the applicant's
 * program. Shares the catalogue fetch, so it costs nothing extra.
 */
export function useProgram(identifier: string | null | undefined) {
  const { programs, defaults, loading, error, refresh } = usePrograms();
  const program = useMemo(() => findProgram(programs, identifier), [programs, identifier]);
  return { program, defaults, loading, error, refresh };
}

export default usePrograms;
